'use client';

import { useCallback, useState } from 'react';

/** Map of resizable panel id to its size (percentage), as used by react-resizable-panels. */
type PanelLayout = Record<string, number>;

/**
 * Persists a resizable panel group's layout in localStorage so the member's chosen panel sizes
 * are respected across page loads. Falls back to the provided default when nothing is stored.
 *
 * @param storageKey - Unique localStorage key for this panel group.
 * @param defaultLayout - Default size map used on first load or when reading fails.
 */
export function usePersistedLayout(storageKey: string, defaultLayout: PanelLayout) {
  const [layout, setLayout] = useState<PanelLayout>(() => {
    if (typeof window === 'undefined') {
      return defaultLayout;
    }

    try {
      const stored = window.localStorage.getItem(storageKey);
      if (!stored) {
        return defaultLayout;
      }
      const parsed = JSON.parse(stored) as PanelLayout;
      return { ...defaultLayout, ...parsed };
    } catch {
      return defaultLayout;
    }
  });

  const handleLayoutChanged = useCallback(
    (next: PanelLayout) => {
      setLayout(next);
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // Ignore write failures (e.g. storage disabled or full).
      }
    },
    [storageKey]
  );

  return { layout, handleLayoutChanged };
}
